import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2, 
  Search, 
  Lightbulb, 
  Settings, 
  Rocket, 
  HeartHandshake,
  Target,
  Users,
  BarChart3,
  Linkedin,
  Quote,
  MapPin
} from 'lucide-react';

const FOUNDER_IMAGE = "/images/team/harpal.webp";

export const ApproachPage = () => {
  return (
    <div data-testid="approach-page">
      {/* Hero */}
      <section className="section-padding bg-[#0A2540]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Our Approach</span>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              A Partnership, Not Just a Project
            </h1>
            <p className="text-white/70 text-lg">
              We believe in building lasting relationships with our clients. Our proven methodology ensures your success from day one and continues to deliver value for years.
            </p>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mb-6">
              Our 5-Phase Methodology
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A structured approach that delivers predictable results while adapting to your unique needs
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                phase: "01",
                title: "Discovery & Assessment",
                icon: Search,
                duration: "1-2 Weeks",
                description: "We dive deep into your business to understand your challenges, goals, and opportunities.",
                activities: [
                  "Stakeholder interviews",
                  "Process documentation",
                  "Technology audit",
                  "Competitive analysis",
                  "Opportunity identification"
                ]
              },
              {
                phase: "02",
                title: "Strategy & Planning",
                icon: Lightbulb,
                duration: "1 Week",
                description: "We create a detailed roadmap with clear milestones, deliverables, and success metrics.",
                activities: [
                  "Solution architecture",
                  "ROI projections",
                  "Risk assessment",
                  "Timeline planning",
                  "Resource allocation"
                ]
              },
              {
                phase: "03",
                title: "Implementation",
                icon: Settings,
                duration: "4-8 Weeks",
                description: "Agile delivery with regular check-ins ensures we stay aligned with your evolving needs.",
                activities: [
                  "Sprint-based development",
                  "Weekly progress reviews",
                  "Iterative refinement",
                  "Quality assurance",
                  "User acceptance testing"
                ]
              },
              {
                phase: "04",
                title: "Launch & Training",
                icon: Rocket,
                duration: "1 Week",
                description: "Smooth go-live with comprehensive training to ensure your team is confident and capable.",
                activities: [
                  "Deployment planning",
                  "Data migration",
                  "User training sessions",
                  "Documentation delivery",
                  "Go-live support"
                ]
              },
              {
                phase: "05",
                title: "Ongoing Optimization",
                icon: HeartHandshake,
                duration: "Continuous",
                description: "Our relationship doesn't end at launch. We continuously monitor and optimize for better results.",
                activities: [
                  "Performance monitoring",
                  "Quarterly business reviews",
                  "Continuous improvement",
                  "Feature enhancements",
                  "Strategic guidance"
                ]
              }
            ].map((phase, index) => (
              <div 
                key={index}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
                data-testid={`phase-${index}`}
              >
                <div className="lg:col-span-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-[#2563EB] rounded-xl flex items-center justify-center flex-shrink-0">
                      <phase.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <span className="text-sm text-[#2563EB] font-medium">Phase {phase.phase}</span>
                      <h3 className="font-outfit text-xl font-semibold text-[#0A2540]">{phase.title}</h3>
                      <span className="text-sm text-slate-500">{phase.duration}</span>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <div className="bg-slate-50 p-8 rounded-2xl">
                    <p className="text-slate-700 mb-6">{phase.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {phase.activities.map((activity, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-[#2563EB]" />
                          {activity}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mb-6">
              Guiding Principles
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: "Results-Focused",
                description: "Every decision is driven by measurable business outcomes. We don't implement technology for technology's sake."
              },
              {
                icon: Users,
                title: "Human-Centered",
                description: "Technology should serve people, not the other way around. We design solutions that your team will actually use."
              },
              {
                icon: BarChart3,
                title: "Data-Driven",
                description: "We let data guide our decisions, measuring everything and continuously optimizing based on real results."
              }
            ].map((principle, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-slate-100">
                <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mb-6">
                  <principle.icon className="w-6 h-6 text-[#2563EB]" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3">{principle.title}</h3>
                <p className="text-slate-600">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Founder / CEO Section */}
      <section className="section-padding bg-white relative overflow-hidden" data-testid="founder-section">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Meet the Founder</span>
              <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 leading-tight">
                The Person Behind Blue Ribbon
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* LEFT: Founder photo + identity */}
              <div className="lg:col-span-4">
                <motion.div
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
                    <img
                      src={FOUNDER_IMAGE}
                      alt="Founder of Blue Ribbon Technologies"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="mt-6 bg-slate-50 border border-slate-100 rounded-2xl p-5">
                    <div className="font-outfit text-lg font-semibold text-[#0A2540]">Founder & CEO</div>
                    <div className="text-sm text-slate-500 mt-1">Blue Ribbon Technologies</div>
                    <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-2">
                      <MapPin className="w-4 h-4" /> Vancouver, BC
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-xl font-bold text-[#2563EB]">25+</div>
                        <div className="text-xs text-slate-500">Years in software</div>
                      </div>
                      <div>
                        <div className="text-xl font-bold text-[#2563EB]">70+</div>
                        <div className="text-xs text-slate-500">Businesses served</div>
                      </div>
                    </div>

                    <a
                      href="https://www.linkedin.com/company/blueribbon-technologies"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#2563EB] hover:underline"
                      data-testid="founder-linkedin-link"
                    >
                      <Linkedin className="w-4 h-4" />
                      Connect on LinkedIn
                    </a>
                  </div>
                </motion.div>
              </div>

              {/* RIGHT: LinkedIn-style story */}
              <motion.div
                className="lg:col-span-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div className="relative">
                  <Quote className="absolute -top-4 -left-4 w-16 h-16 text-[#2563EB] opacity-15" />

                  <div className="relative space-y-5 text-slate-700 text-lg leading-relaxed">
                    <p>
                      With over two decades of experience, I have brought executive and technical leadership into shepherding enterprises through progressive growth stages, reaching and exceeding milestones, and offering real value along the way.
                    </p>
                    <p>
                      Bringing order and scale to businesses that encounter complex operational challenges is something I deeply and practically care about. New opportunities in technology and media keep me on my toes and push me to create better solutions for clients across the globe.
                    </p>
                    <p>
                      While launching projects from concept to finish and defining the roadmap for organizations, I realized that an integrated approach and a crisp implementation of the tech stack is crucial for realizing comprehensive business objectives.
                    </p>
                    <p className="text-[#0A2540] font-medium">
                      With a vision to build a powerhouse agency that provides bespoke, strategic solutions to businesses, I founded Blue Ribbon Technologies.
                    </p>
                    <p>
                      Through BRT, our aim is to translate project visions and goals into reality, delivering tangible results in line with client expectations. Not just tech for tech&rsquo;s sake, real business impact that clients can see and measure.
                    </p>
                  </div>

                  {/* Signature-style closing */}
                  <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-10 bg-[#2563EB] rounded-full" />
                      <div>
                        <div className="font-outfit text-[#0A2540] font-medium">Founder&rsquo;s note</div>
                        <div className="text-slate-500 text-sm">Blue Ribbon Technologies, Vancouver</div>
                      </div>
                    </div>
                    <Link to="/contact">
                      <Button
                        variant="outline"
                        className="border-2 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white rounded-full px-6 py-5 text-sm"
                        data-testid="founder-cta-button"
                      >
                        Let&rsquo;s talk growth
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
        <div className="container-custom text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Experience the Difference?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Start with a discovery call to see how our approach can work for your business.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#2563EB] hover:bg-slate-100 rounded-full px-8 py-6 text-lg">
              Book a Discovery Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ApproachPage;
